import type {
  ComponentType,
  Dispatch,
  ReactElement,
  SetStateAction,
} from "react";

import type { ChartOptions } from "chart.js";
import type { LatLngTuple } from "leaflet";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import type { BrandIconProps } from "simplydo/react/ui/brand-icon";
import { IconData } from "simplydo/react/ui/icon";

import type { CommentType } from "components/lib/Discussion/Discussion";

export type InnointelCompany = OpenAPI.Schemas["InnointelCompanies"];

export type CompanyNewsItem = NonNullable<InnointelCompany["news"]>[number];
export type CompanyBlog = NonNullable<InnointelCompany["blogs"]>[number];
export type CompanyPatent = NonNullable<
  NonNullable<InnointelCompany["patents"]>["items"]
>[number];
export type CompanyCertification = NonNullable<
  InnointelCompany["certifications"]
>[number];
export type CompanyAiService = NonNullable<
  NonNullable<InnointelCompany["ai"]>["services"]
>[number];
export type CompanyCreditsafe = NonNullable<InnointelCompany["creditsafe"]>;

/** OpenAPI models shareholders as empty objects; runtime returns full records. */
export type CompanyShareholder = {
  name: string;
  shareholderType: string;
  totalNumberOfSharesOwned: number;
  percentSharesHeld?: number | string;
  shareType?: string;
};

/** Messages enriched at runtime with tracking and sender details. */
export type CompanyMessage = OpenAPI.Schemas["CompanyMessage"] & {
  replyTo?: string;
  ownerUser?: OpenAPI.Schemas["PreparedUser"];
  emailLogs?: {
    sent?: boolean;
    events?: { delivered?: boolean; open?: boolean; bounced?: boolean };
  }[];
};

export type CompanyNote =
  OpenAPI.GET<"/innointel/companies/{company_id}/notes">["response"]["notes"][number];

export type CreditsafeSearchCompany =
  OpenAPI.GET<"/verifications/companies/search">["response"]["companies"][number] & {
    statusDescription?: string;
  };

export function parseShareholders(
  shareholders?: Record<string, never>[],
): CompanyShareholder[] {
  if (!shareholders?.length) {
    return [];
  }

  return shareholders.flatMap((shareholder) => {
    const record = shareholder as Partial<CompanyShareholder>;
    if (
      !record.name ||
      !record.shareholderType ||
      record.totalNumberOfSharesOwned === undefined
    ) {
      return [];
    }

    return [
      {
        name: record.name,
        shareholderType: record.shareholderType,
        totalNumberOfSharesOwned: record.totalNumberOfSharesOwned,
        percentSharesHeld: record.percentSharesHeld,
        shareType: record.shareType,
      },
    ];
  });
}

export type CompanyInfoTab =
  | "overview"
  | "locations"
  | "shareholders"
  | "news"
  | "blogs"
  | "messages";

export type CompanyInfoRequirementKey =
  | "hasShareholders"
  | "hasNews"
  | "hasBlogs";

export type CompanyInfoRequirements = Record<
  CompanyInfoRequirementKey,
  boolean
>;

export type CreditRatingValue = "A" | "B" | "C" | "D" | "E";

/** LinkedIn profile fields used in overview (not on base company schema). */
export function mapNoteToComment(note: CompanyNote): CommentType {
  return {
    _id: note._id,
    comment: note.comment,
    user: note.user,
    createdAt: note.createdAt,
  };
}

export type CompanySummary = OpenAPI.Schemas["companies"];
export type InnointelCompanySources = NonNullable<CompanySummary["sources"]>;
export type CompanySourceInfo = {
  name: string;
  icon?: IconData;
  BrandIcon?: ComponentType<BrandIconProps>;
  favicon?: string;
  url?: string;
};

/** Search payloads and dashboard summaries share this list-row shape at runtime. */
export type InnointelCompanyListItem = (InnointelCompany | CompanySummary) & {
  sources?: InnointelCompanySources;
  sicCodes?: string[];
  surfacedFrom?: string;
  status?: InnointelCompany["status"];
  via?: string[];
  lifecycle?: InnointelCompany["lifecycle"];
  searchState?: InnointelCompany["searchState"];
  brandGroup?: InnointelCompany["brandGroup"];
  contact?: InnointelCompany["contact"];
  patents?: InnointelCompany["patents"];
};

export type InnointelDashboard = OpenAPI.Schemas["InnointelDashboard"];
export type DashboardDetail =
  OpenAPI.GET<"/innointel/dashboards/{dashboard_id}">["response"];
export type DashboardListResponse =
  OpenAPI.GET<"/innointel/dashboards">["response"];

export type DashboardSectorialEvent = NonNullable<
  InnointelDashboard["sectorialEvents"]
>[number];

export type DashboardNewsItem = NonNullable<
  InnointelDashboard["sectorialNews"]
>[number];

export type DashboardTrendingNewsItem = NonNullable<
  InnointelDashboard["trendingNews"]
>[number] & { link?: string };

export type DashboardScholarlyArticle = NonNullable<
  InnointelDashboard["scholarlyArticles"]
>[number];

export type DashboardPolicy = NonNullable<
  InnointelDashboard["policies"]
>[number];

export type FundingResearchProject = NonNullable<
  NonNullable<InnointelDashboard["fundingResearch"]>["projects"]
>[number];

export type FundingProjectDisplay = {
  title?: string;
  grantCategory?: string;
  url?: string;
  fund?: { valuePounds?: number; start?: string; end?: string };
};

export type LatLng = { lat?: number; lng?: number };

export type MapBounds = [[number, number], [number, number]];

export type MapState = { lat: number; lng: number; zoom: number };

type CoordinatePair = { latitude?: number; longitude?: number };

export function toFundingProjectDisplay(
  entry: FundingResearchProject,
): FundingProjectDisplay {
  return {
    title: entry.project?.title,
    grantCategory: entry.project?.grantCategory,
    url: entry.project?.url,
    fund: entry.project?.fund,
  };
}

export function toMapBounds(
  bounds: CoordinatePair[] | undefined,
): MapBounds | null {
  const southWest = bounds?.[0];
  const northEast = bounds?.[1];
  if (
    southWest?.latitude === undefined ||
    southWest.longitude === undefined ||
    northEast?.latitude === undefined ||
    northEast.longitude === undefined
  ) {
    return null;
  }
  return [
    [southWest.latitude, southWest.longitude],
    [northEast.latitude, northEast.longitude],
  ];
}

export function hasMapCoordinates(
  company: InnointelCompany,
): company is InnointelCompany & {
  location: {
    search: { coordinates: { latitude: number; longitude: number } };
  };
} {
  const coordinates = company.location?.search?.coordinates;
  return (
    coordinates?.latitude !== undefined && coordinates.longitude !== undefined
  );
}

export function lookupRecordValue<Key extends string, Value>(
  map: Record<Key, Value>,
  key: string | number | undefined,
): Value | undefined {
  if (key === undefined) {
    return undefined;
  }
  return map[key as Key];
}

// --- List, search, and navigation types ---

export type InnoIntelList = OpenAPI.Schemas["InnointelList"] & {
  subscribed?: boolean;
};
export type InnoIntelSearch = OpenAPI.Schemas["InnointelSearch"];
export type InnoIntelCompanyDetail =
  OpenAPI.GET<"/innointel/companies/{company_id}">["response"]["company"];

/** Route-level company table state (OpenAPI path query is `never`; use operation shape). */
export type InnoIntelCompanySearchParams = {
  filters: string[];
  page: number;
  query: string;
  sort: string;
};

export type InnoIntelSearchCompanyVariant =
  | "included"
  | "excluded"
  | "to-process"
  | "rejected";

export type InnoIntelSearchCompanyQueryParams = InnoIntelCompanySearchParams & {
  variant: InnoIntelSearchCompanyVariant;
};

export type InnoIntelListCompaniesResponse =
  OpenAPI.GET<"/innointel/lists/{list_id}/companies">["response"];

export type InnoIntelSearchCompaniesResponse =
  OpenAPI.GET<"/innointel/searches/{search_id}/companies">["response"];

export type InnoIntelListState = {
  listId: string | undefined;
  listTab: string;
  lists: InnoIntelList[] | undefined;
  list: InnoIntelList | undefined;
  companies: InnointelCompanyListItem[] | undefined;
  totalCompanyPages: number | undefined;
  searchParams: InnoIntelCompanySearchParams;
  setSearchParams: Dispatch<SetStateAction<InnoIntelCompanySearchParams>>;
  addCompanies: (listId: string, companyIds: string[]) => void;
  isAddingCompanies: boolean;
  removeCompanies: (listId: string, companyIds: string[]) => void;
  isRemovingCompanies: boolean;
  createList: (listName: string, companyIds?: string[]) => void;
  isCreatingList: boolean;
  updateList: (listId: string, listName: string) => void;
  isUpdatingList: boolean;
  deleteList: (listId: string) => void;
  isDeletingList: boolean;
  duplicateList: (listId: string, listName: string) => void;
  isDuplicatingList: boolean;
  subscribeToList: (listId: string) => void;
  isSubscribingToList: boolean;
  unsubscribeFromList: (listId: string) => void;
  isUnsubscribingFromList: boolean;
};

export type InnoIntelSearchState = {
  searchId: string | undefined;
  searchTab: string;
  search: InnoIntelSearch | undefined;
  searchError?: string;
  companies: InnointelCompanyListItem[] | undefined;
  totalCompanyPages: number | undefined;
  collapseBranches: boolean;
  setCollapseBranches: (collapse: boolean) => void;
  searchParams: InnoIntelCompanySearchParams;
  setSearchParams: Dispatch<SetStateAction<InnoIntelCompanySearchParams>>;
  includeCompanies: (companyIds: string[]) => void;
  excludeCompanies: (companyIds: string[]) => void;
  processCompanies: (companyIds: string[]) => void;
  processNextCompanies: (count: number) => void;
  isProcessing: boolean;
  cancelSearch: () => void;
  isCancellingSearch: boolean;
};

export type InnoIntelListAnalysis =
  OpenAPI.GET<"/innointel/lists/{list_id}/analysis">["response"];

export type InnoIntelListAnalysisData = Omit<
  InnoIntelListAnalysis,
  "foundingDates"
> & { foundingDates: Record<string, number> | string[] };

export type InnoIntelAnalysisLocation =
  InnoIntelListAnalysisData["locations"][number];

export type InnoIntelListNewsResponse =
  OpenAPI.GET<"/innointel/lists/{list_id}/news">["response"];

export type InnoIntelListNewsEntry =
  InnoIntelListNewsResponse["entries"][number] & {
    author?: string;
    publisher?: string;
  };

export type InnoIntelListLog = {
  type?:
    | "addedCompany"
    | "removedCompany"
    | "updated"
    | "sharedWith"
    | "unsharedWith";
  createdAt?: string;
  user?: string;
  targetUser?: string;
  targetCompany?: string;
  ownerTargetUser?: OpenAPI.Schemas["PreparedUser"] & {
    profile: OpenAPI.Schemas["PreparedUser"]["profile"] & {
      avatarUrl?: string;
    };
  };
  ownerTargetCompany?: InnointelCompany;
  ownerUser?: OpenAPI.Schemas["PreparedUser"];
};

export type ListTabKey =
  | "companies"
  | "news"
  | "analysis"
  | "logs"
  | "roles"
  | "settings";

export const listTabIndexMap: Record<ListTabKey, number> = {
  companies: 0,
  news: 1,
  analysis: 2,
  logs: 3,
  roles: 4,
  settings: 5,
};

export type SearchTabKey =
  | "included"
  | "excluded"
  | "to-process"
  | "rejected"
  | "settings";

export const searchTabIndexMap: Record<SearchTabKey, number> = {
  included: 0,
  excluded: 1,
  "to-process": 2,
  rejected: 3,
  settings: 4,
};

export type InnoIntelLineChartDataset = { label: string; points: number[] };

export type InnoIntelLineChartData = {
  labels: string[];
  datasets: {
    borderColor: string;
    pointBackgroundColor: string;
    backgroundColor: string;
    data: number[];
    label: string;
    pointRadius: number;
  }[];
};

export type InnoIntelLineChartOptions = ChartOptions<"line">;
export type InnoIntelBarChartOptions = ChartOptions<"bar">;

export const mapDefaultCenter: LatLngTuple = [25, 0.2];

export type InnoIntelPowerBiConnection = {
  contextId?: string;
  datasets?: InnoIntelPowerBiDataset[];
};

export type InnoIntelPowerBiDataset = {
  type: string;
  workspaceId: string;
  workspaceName: string;
  dataset: string;
};

export type InnoIntelPowerBiWorkspace = {
  id: string | null;
  isOnDedicatedCapacity: boolean;
  isReadOnly: boolean;
  name: string;
  type: "Workspace";
};

export type InnoIntelExcelConnection = {
  contextId?: string;
  driveItemUrl?: string;
};

export type InnoIntelUserServices = NonNullable<UserMe["services"]> & {
  powerbi?: { connections?: InnoIntelPowerBiConnection[] };
  microsoftExcel?: { connections?: InnoIntelExcelConnection[] };
};

export type InnoIntelUser = UserMe & { services?: InnoIntelUserServices };

export type CompanyPdfCompany = OpenAPI.Schemas["Company"] &
  InnoIntelCompanyDetail;
